import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { fmtMonto } from 'shared/format';
import API from 'shared/api';
import { useAuth } from 'shared/context/AuthContext';
import { useDarkMode } from 'shared/context/DarkModeContext';
import { useNotificaciones } from 'shared/context/NotificacionesContext';
import useWindowSize from 'shared/hooks/useWindowSize';
import {
  FiPlus, FiX, FiPercent, FiUserPlus, FiUserMinus, FiCheck, FiXCircle,
  FiSearch, FiMapPin, FiMail, FiLock, FiUser, FiBriefcase, FiTrash2, FiDollarSign,
} from 'react-icons/fi';

const money = (n) => `$${fmtMonto(n)}`;

export default function Vendedores() {
  const { isAdmin, isSupervisor, tienePermiso } = useAuth();
  const { notificar } = useNotificaciones();
  const { COL } = useDarkMode();
  const { width } = useWindowSize();
  const isMobile = width < 768;

  const [vendedores, setVendedores] = useState([]);
  const [comisiones, setComisiones] = useState([]);
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [cobrosPendientes, setCobrosPendientes] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [tab, setTab] = useState('vendedores');
  const [modalNuevo, setModalNuevo] = useState(false);
  const [modalClientes, setModalClientes] = useState(null);
  const [eliminando, setEliminando] = useState(null);
  const [modalLiquidar, setModalLiquidar] = useState(false);

  const puedeGestionar = isAdmin() || isSupervisor() || tienePermiso('vendedores.gestionar');
  const puedeVerificar = isAdmin() || tienePermiso('credito.gestionar');
  const puedeLiquidar = isAdmin() || isSupervisor() || tienePermiso('vendedores.comisiones.liquidar');

  const cargar = useCallback(async () => {
    try {
      const [rV, rC, rL] = await Promise.all([
        API.get('/vendedores/listar'),
        API.get('/vendedores/comisiones'),
        API.get('/vendedores/liquidaciones'),
      ]);
      if (rV.data.status === 'success') setVendedores(rV.data.data);
      if (rC.data.status === 'success') setComisiones(rC.data.data);
      if (rL.data.status === 'success') setLiquidaciones(rL.data.data);
    } catch (e) {
      notificar({ titulo: 'Error', mensaje: 'No se pudo cargar vendedores', tipo: 'error' });
    } finally {
      setCargando(false);
    }
  }, [notificar]);

  const cargarCobrosPendientes = useCallback(async () => {
    if (!puedeVerificar) return;
    try {
      const r = await API.get('/vendedores/cobros-pendientes');
      if (r.data.status === 'success') setCobrosPendientes(r.data.data);
    } catch (e) {
      /* noop */
    }
  }, [puedeVerificar]);

  useEffect(() => { cargar(); cargarCobrosPendientes(); }, [cargar, cargarCobrosPendientes]);

  const verificarCobro = async (abonoId, aprobado) => {
    try {
      await API.put(`/credito/abono/${abonoId}/verificar`, { aprobado });
      setCobrosPendientes((prev) => prev.filter((p) => p.id !== abonoId));
      cargar();
      notificar({ titulo: aprobado ? 'Cobro aprobado' : 'Cobro rechazado', tipo: 'success' });
    } catch (e) {
      notificar({ titulo: 'Error', mensaje: e.response?.data?.detail || 'No se pudo verificar', tipo: 'error' });
    }
  };

  const guardarEdicion = async (cedula, pct, activo) => {
    try {
      await API.put(`/vendedores/${cedula}`, { porcentaje_comision: parseFloat(pct), activo });
      notificar({ titulo: 'Vendedor actualizado', tipo: 'success' });
      cargar();
    } catch (e) {
      notificar({ titulo: 'Error', mensaje: e.response?.data?.detail || 'No se pudo guardar', tipo: 'error' });
    }
  };

  const eliminarVendedor = async () => {
    if (!eliminando) return;
    try {
      await API.delete(`/vendedores/${eliminando.cedula}`);
      notificar({ titulo: 'Vendedor eliminado', mensaje: 'Sus clientes quedaron libres', tipo: 'success' });
      setEliminando(null);
      cargar();
    } catch (e) {
      notificar({ titulo: 'Error', mensaje: e.response?.data?.detail || 'No se pudo eliminar', tipo: 'error' });
    }
  };

  const S = useMemo(() => buildStyles(COL), [COL]);

  if (cargando) return <div style={S.wrap}>Cargando...</div>;

  const tabs = [
    { id: 'vendedores', label: 'Vendedores' },
    { id: 'comisiones', label: 'Comisiones' },
    ...(puedeVerificar ? [{ id: 'verificar', label: 'Cobros por verificar', badge: cobrosPendientes.length }] : []),
    ...(puedeLiquidar ? [{ id: 'liquidaciones', label: 'Liquidaciones' }] : []),
  ];

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <div>
          <h2 style={S.title}>Vendedores</h2>
          <div style={S.subtitle}>Crea vendedores, asigna clientes y define sus comisiones por cobro</div>
        </div>
        {puedeGestionar && (
          <button style={S.btn} onClick={() => setModalNuevo(true)}>
            <FiPlus /> Nuevo vendedor
          </button>
        )}
      </div>

      <div style={S.tabs}>
        {tabs.map((t) => (
          <button
            key={t.id}
            style={{ ...S.tabBtn, ...(tab === t.id ? S.tabBtnActive : {}) }}
            onClick={() => { setTab(t.id); if (t.id === 'verificar') cargarCobrosPendientes(); }}
          >
            {t.label}
            {t.badge > 0 && <span style={S.badge}>{t.badge}</span>}
          </button>
        ))}
      </div>

      {tab === 'vendedores' && (
        <div style={S.card}>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Vendedor</th>
                  <th style={S.th}>Zona</th>
                  <th style={S.th}>% Comisión</th>
                  <th style={S.th}>Clientes</th>
                  <th style={S.th}>Pendiente</th>
                  <th style={S.th}>Pagada</th>
                  <th style={S.th}>Estado</th>
                  <th style={S.th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {vendedores.map((v) => (
                  <tr key={v.cedula}>
                    <td style={S.td}>
                      <strong>{v.nombre || v.cedula}</strong>
                      <div style={S.tdSub}>{v.cedula}</div>
                    </td>
                    <td style={S.td}>{v.zona_nombre || '—'}</td>
                    <td style={S.td}>
                      <PercentCell v={v} COL={COL} onSave={guardarEdicion} puedeGestionar={puedeGestionar} />
                    </td>
                    <td style={S.td}>{v.num_clientes}</td>
                    <td style={S.td}><span style={{ color: COL.amber, fontWeight: 600 }}>{money(v.comision_pendiente)}</span></td>
                    <td style={S.td}><span style={{ color: COL.green, fontWeight: 600 }}>{money(v.comision_pagada)}</span></td>
                    <td style={S.td}>
                      <span style={{ ...S.pill, background: v.activo ? COL.greenBg : COL.redBg, color: v.activo ? COL.green : COL.red }}>
                        {v.activo ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td style={S.td}>
                      <div style={S.actions}>
                        <button style={S.btnGhost} onClick={() => setModalClientes(v)}><FiUserPlus /> Clientes</button>
                        {puedeGestionar && (
                          <button style={S.btnGhost} onClick={() => guardarEdicion(v.cedula, v.porcentaje_comision, !v.activo)}>
                            {v.activo ? 'Desactivar' : 'Activar'}
                          </button>
                        )}
                        {puedeGestionar && (
                          <button style={{ ...S.btnGhost, color: COL.red, borderColor: COL.red }} onClick={() => setEliminando(v)}>
                            <FiTrash2 /> Eliminar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {vendedores.length === 0 && (
                  <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', color: COL.muted }}>No hay vendedores todavía</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'comisiones' && (
        <div style={S.card}>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Vendedor</th>
                  <th style={S.th}>Cliente</th>
                  <th style={S.th}>Cobro</th>
                  <th style={S.th}>%</th>
                  <th style={S.th}>Comisión</th>
                  <th style={S.th}>Fecha</th>
                  <th style={S.th}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {comisiones.map((c) => (
                  <tr key={c.id}>
                    <td style={S.td}>{c.vendedor_nombre || c.vendedor_cedula}</td>
                    <td style={S.td}>{c.cliente_nombre || '-'}</td>
                    <td style={S.td}>{money(c.monto_cobro)}</td>
                    <td style={S.td}>{Number(c.porcentaje || 0)}%</td>
                    <td style={S.td}><strong>{money(c.monto_comision)}</strong></td>
                    <td style={S.td}>{c.fecha_cobro}</td>
                    <td style={S.td}><span style={{ ...S.pill, background: COL.blueBg, color: COL.pri2 }}>{c.estado}</span></td>
                  </tr>
                ))}
                {comisiones.length === 0 && (
                  <tr><td colSpan={7} style={{ ...S.td, textAlign: 'center', color: COL.muted }}>Sin comisiones</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'verificar' && (
        <div style={S.card}>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Fecha</th>
                  <th style={S.th}>Cliente</th>
                  <th style={S.th}>Vendedor</th>
                  <th style={S.th}>Monto</th>
                  <th style={S.th}>Método</th>
                  <th style={S.th}>Referencia</th>
                  <th style={S.th}>Registró</th>
                  <th style={S.th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {cobrosPendientes.map((p) => (
                  <tr key={p.id}>
                    <td style={S.td}>{p.fecha ? new Date(p.fecha).toLocaleString() : '-'}</td>
                    <td style={S.td}>
                      <strong>{p.cliente_nombre || p.cliente_empresa || '-'}</strong>
                      {p.cliente_apellido ? ` ${p.cliente_apellido}` : ''}
                    </td>
                    <td style={S.td}>{p.vendedor_nombre || <span style={{ color: COL.muted }}>—</span>}</td>
                    <td style={S.td}><strong>{money(p.monto)}</strong></td>
                    <td style={S.td}>{p.metodo_pago || '-'}</td>
                    <td style={S.td}>{p.referencia || '-'}</td>
                    <td style={S.td}>{p.registrador_nombre || p.registrado_por || '-'}</td>
                    <td style={S.td}>
                      <div style={S.actions}>
                        <button style={{ ...S.btnGhost, color: COL.green, borderColor: COL.green }} onClick={() => verificarCobro(p.id, true)}><FiCheck /> Aprobar</button>
                        <button style={{ ...S.btnGhost, color: COL.red, borderColor: COL.red }} onClick={() => verificarCobro(p.id, false)}><FiXCircle /> Rechazar</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {cobrosPendientes.length === 0 && (
                  <tr><td colSpan={8} style={{ ...S.td, textAlign: 'center', color: COL.muted }}>No hay cobros pendientes de verificación</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'liquidaciones' && (
        <LiquidacionesTab
          COL={COL}
          S={S}
          liquidaciones={liquidaciones}
          vendedores={vendedores}
          puedeLiquidar={puedeLiquidar}
          onNueva={() => setModalLiquidar(true)}
          onRefresh={cargar}
        />
      )}

      {modalNuevo && (
        <ModalNuevoVendedor
          COL={COL}
          isMobile={isMobile}
          onClose={() => setModalNuevo(false)}
          onCreated={() => { setModalNuevo(false); cargar(); }}
        />
      )}

      {modalClientes && (
        <ModalClientes
          COL={COL}
          isMobile={isMobile}
          vendedor={modalClientes}
          onClose={() => setModalClientes(null)}
          onChanged={() => cargar()}
        />
      )}

      {modalLiquidar && (
        <ModalLiquidar
          COL={COL}
          isMobile={isMobile}
          vendedores={vendedores}
          onClose={() => setModalLiquidar(false)}
          onCreated={() => { setModalLiquidar(false); cargar(); }}
        />
      )}

      {eliminando && (
        <Modal COL={COL} isMobile={isMobile} onClose={() => setEliminando(null)} title="Eliminar vendedor" width={440}>
          <p style={{ fontSize: 14, color: COL.text, lineHeight: 1.6, margin: '0 0 8px' }}>
            ¿Eliminar a <strong>{eliminando.nombre || eliminando.cedula}</strong>?
          </p>
          <p style={{ fontSize: 13, color: COL.muted, lineHeight: 1.6, margin: 0 }}>
            Sus clientes quedarán sin vendedor asignado. El historial de comisiones se conserva como registro.
          </p>
          <div style={S.modalFooter}>
            <button style={S.btnGhost} onClick={() => setEliminando(null)}>Cancelar</button>
            <button style={{ ...S.btn, background: COL.red }} onClick={eliminarVendedor}><FiTrash2 /> Eliminar</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function LiquidacionesTab({ COL, S, liquidaciones, vendedores, puedeLiquidar, onNueva, onRefresh }) {
  const { notificar } = useNotificaciones();

  const cambiarEstado = async (id, estado) => {
    try {
      await API.put(`/vendedores/liquidaciones/${id}/estado`, { estado });
      notificar({ titulo: estado === 'pagada' ? 'Comisiones pagadas' : 'Liquidación confirmada', tipo: 'success' });
      onRefresh();
    } catch (e) {
      notificar({ titulo: 'Error', mensaje: e.response?.data?.detail || 'No se pudo actualizar', tipo: 'error' });
    }
  };

  const estadoTag = (estado) => {
    const map = {
      borrador: { bg: COL.amberBg, color: COL.amber, label: 'Borrador' },
      confirmada: { bg: COL.blueBg, color: COL.pri2, label: 'Confirmada' },
      pagada: { bg: COL.greenBg, color: COL.green, label: 'Pagada' },
    };
    const t = map[estado] || map.borrador;
    return <span style={{ ...S.pill, background: t.bg, color: t.color }}>{t.label}</span>;
  };

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 14px 14px' }}>
        <div style={{ fontSize: 13, color: COL.muted }}>
          Registra el pago de comisiones para que queden sin pendiente.
        </div>
        {puedeLiquidar && (
          <button style={S.btn} onClick={onNueva}><FiDollarSign /> Nueva liquidación</button>
        )}
      </div>
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Vendedor</th>
              <th style={S.th}>Desde</th>
              <th style={S.th}>Hasta</th>
              <th style={S.th}>Monto</th>
              <th style={S.th}>Comisiones</th>
              <th style={S.th}>Estado</th>
              <th style={S.th}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {liquidaciones.map((l) => (
              <tr key={l.id}>
                <td style={S.td}><strong>{l.vendedor_nombre || l.vendedor_cedula}</strong></td>
                <td style={S.td}>{l.fecha_desde || '-'}</td>
                <td style={S.td}>{l.fecha_hasta || '-'}</td>
                <td style={S.td}><strong>{money(l.monto_total)}</strong></td>
                <td style={S.td}>{l.num_comisiones || 0}</td>
                <td style={S.td}>{estadoTag(l.estado)}</td>
                <td style={S.td}>
                  <div style={S.actions}>
                    {puedeLiquidar && l.estado === 'borrador' && (
                      <button style={{ ...S.btnGhost, color: COL.pri2, borderColor: COL.pri2 }} onClick={() => cambiarEstado(l.id, 'confirmada')}>
                        <FiCheck /> Confirmar
                      </button>
                    )}
                    {puedeLiquidar && l.estado !== 'pagada' && (
                      <button style={{ ...S.btnGhost, color: COL.green, borderColor: COL.green }} onClick={() => cambiarEstado(l.id, 'pagada')}>
                        <FiDollarSign /> Marcar pagada
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {liquidaciones.length === 0 && (
              <tr><td colSpan={7} style={{ ...S.td, textAlign: 'center', color: COL.muted }}>No hay liquidaciones todavía</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ModalLiquidar({ COL, isMobile, vendedores, onClose, onCreated }) {
  const { notificar } = useNotificaciones();
  const S = useMemo(() => buildStyles(COL), [COL]);
  const [cedula, setCedula] = useState('');
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [preview, setPreview] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!cedula || !desde || !hasta) { setPreview(null); return; }
    API.get('/vendedores/comisiones', { params: { vendedor_cedula: cedula, estado: 'devengada', fecha_desde: desde, fecha_hasta: hasta, limite: 1000 } })
      .then((r) => {
        if (r.data.status === 'success') {
          const rows = r.data.data;
          setPreview({ n: rows.length, total: rows.reduce((s, c) => s + Number(c.monto_comision || 0), 0) });
        }
      })
      .catch(() => setPreview(null));
  }, [cedula, desde, hasta]);

  const liquidar = async () => {
    if (!cedula || !desde || !hasta) {
      notificar({ titulo: 'Faltan datos', mensaje: 'Selecciona vendedor y rango de fechas', tipo: 'error' });
      return;
    }
    setGuardando(true);
    try {
      await API.post('/vendedores/liquidar', { vendedor_cedula: cedula, fecha_desde: desde, fecha_hasta: hasta });
      notificar({ titulo: 'Liquidación creada', tipo: 'success' });
      onCreated();
    } catch (e) {
      notificar({ titulo: 'Error', mensaje: e.response?.data?.detail || 'No se pudo liquidar', tipo: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal COL={COL} isMobile={isMobile} onClose={onClose} title="Nueva liquidación de comisiones" width={460}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: COL.muted, marginBottom: 6, display: 'block' }}>Vendedor</label>
          <select style={S.input} value={cedula} onChange={(e) => setCedula(e.target.value)}>
            <option value="">Seleccionar...</option>
            {vendedores.map((v) => <option key={v.cedula} value={v.cedula}>{v.nombre || v.cedula}</option>)}
          </select>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COL.muted, marginBottom: 6, display: 'block' }}>Desde</label>
            <input type="date" style={S.input} value={desde} onChange={(e) => setDesde(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: COL.muted, marginBottom: 6, display: 'block' }}>Hasta</label>
            <input type="date" style={S.input} value={hasta} onChange={(e) => setHasta(e.target.value)} />
          </div>
        </div>
        {preview && (
          <div style={{ padding: 12, borderRadius: 10, background: COL.surfaceAlt, fontSize: 13, color: COL.text }}>
            <strong>{preview.n}</strong> comisiones devengadas · <strong>{money(preview.total)}</strong>
          </div>
        )}
        <div style={S.modalFooter}>
          <button style={S.btnGhost} onClick={onClose}>Cancelar</button>
          <button style={S.btn} onClick={liquidar} disabled={guardando || !cedula || !desde || !hasta}>
            {guardando ? 'Liquidando...' : 'Liquidar'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function PercentCell({ v, COL, onSave, puedeGestionar }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(v.porcentaje_comision);
  useEffect(() => { setVal(v.porcentaje_comision); }, [v.porcentaje_comision]);

  if (!editing) {
    return (
      <span
        onClick={puedeGestionar ? () => setEditing(true) : undefined}
        style={{ fontWeight: 700, color: COL.pri2, cursor: puedeGestionar ? 'pointer' : 'default' }}
        title={puedeGestionar ? 'Clic para editar' : ''}
      >
        {Number(v.porcentaje_comision || 0)}%
      </span>
    );
  }
  return (
    <input
      type="number" step="0.01" min="0" max="100"
      value={val}
      autoFocus
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { setEditing(false); onSave(v.cedula, val, v.activo); }}
      onKeyDown={(e) => { if (e.key === 'Enter') { setEditing(false); onSave(v.cedula, val, v.activo); } if (e.key === 'Escape') setEditing(false); }}
      style={{ width: 72, padding: '6px 8px', borderRadius: 8, border: `1px solid ${COL.border}`, fontSize: 14, color: COL.text, background: COL.inputBg }}
    />
  );
}

function ModalNuevoVendedor({ COL, isMobile, onClose, onCreated }) {
  const { notificar } = useNotificaciones();
  const [sedes, setSedes] = useState([]);
  const [buscandoClientes, setBuscandoClientes] = useState(false);
  const [clientesSeleccionados, setClientesSeleccionados] = useState([]);
  const [form, setForm] = useState({
    nombre: '', cedula: '', usuario: '', correo: '', clave: '', codigo_sede: '', porcentaje_comision: '',
  });
  const [guardando, setGuardando] = useState(false);

  const S = useMemo(() => buildStyles(COL), [COL]);

  useEffect(() => {
    API.get('/usuarios/sedes')
      .then((r) => { if (r.data.status === 'success') setSedes(r.data.data || []); })
      .catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const crear = async () => {
    if (!form.cedula.trim() || !form.nombre.trim() || !form.usuario.trim()) {
      notificar({ titulo: 'Faltan datos', mensaje: 'Cédula, nombre y usuario son obligatorios', tipo: 'error' });
      return;
    }
    setGuardando(true);
    try {
      await API.post('/vendedores/agregar', {
        cedula: form.cedula.trim(),
        nombre: form.nombre.trim(),
        usuario: form.usuario.trim(),
        correo: form.correo.trim(),
        clave: form.clave,
        codigo_sede: form.codigo_sede || null,
        porcentaje_comision: parseFloat(form.porcentaje_comision || 0),
        cliente_ids: clientesSeleccionados.map((c) => c.id),
      });
      notificar({ titulo: 'Vendedor creado', mensaje: 'Usuario, comisión y clientes configurados', tipo: 'success' });
      onCreated();
    } catch (e) {
      notificar({ titulo: 'Error', mensaje: e.response?.data?.detail || 'No se pudo crear', tipo: 'error' });
    } finally {
      setGuardando(false);
    }
  };

  const quitarCliente = (id) => setClientesSeleccionados((prev) => prev.filter((c) => c.id !== id));

  return (
    <Modal COL={COL} isMobile={isMobile} onClose={onClose} title="Nuevo vendedor" width={640}>
      <div style={S.formGrid}>
        <Field icon={<FiUser />} label="Nombre completo *">
          <input style={S.input} value={form.nombre} onChange={set('nombre')} placeholder="Nombre y apellido" />
        </Field>
        <Field icon={<FiBriefcase />} label="Cédula *">
          <input style={S.input} value={form.cedula} onChange={set('cedula')} placeholder="V-12345678" />
        </Field>
        <Field icon={<FiUser />} label="Nombre de usuario *">
          <input style={S.input} value={form.usuario} onChange={set('usuario')} placeholder="usuario" autoCapitalize="none" />
        </Field>
        <Field icon={<FiMail />} label="Correo">
          <input style={S.input} type="email" value={form.correo} onChange={set('correo')} placeholder="correo@empresa.com" />
        </Field>
        <Field icon={<FiLock />} label="Contraseña">
          <input style={S.input} type="password" value={form.clave} onChange={set('clave')} placeholder="Dejar vacío = cambiar123" />
        </Field>
        <Field icon={<FiMapPin />} label="Zona de trabajo">
          <select style={S.input} value={form.codigo_sede} onChange={set('codigo_sede')}>
            <option value="">Seleccionar zona...</option>
            {sedes.map((s) => <option key={s.codigo} value={s.codigo}>{s.nombre}</option>)}
          </select>
        </Field>
        <Field icon={<FiPercent />} label="% de comisión por cobro">
          <input style={S.input} type="number" step="0.01" min="0" max="100" value={form.porcentaje_comision} onChange={set('porcentaje_comision')} placeholder="5" />
        </Field>
      </div>

      <div style={{ marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: COL.muted }}>Clientes asignados ({clientesSeleccionados.length})</span>
          <button style={S.btnGhost} onClick={() => setBuscandoClientes(true)}><FiSearch /> Seleccionar clientes</button>
        </div>
        {clientesSeleccionados.length === 0 ? (
          <div style={{ ...S.empty, marginTop: 8 }}>Aún no has seleccionado clientes</div>
        ) : (
          <div style={S.chips}>
            {clientesSeleccionados.map((c) => (
              <span key={c.id} style={S.chip}>
                {c.nombre || c.nombre_empresa || c.id}{c.apellido ? ` ${c.apellido}` : ''}
                <button onClick={() => quitarCliente(c.id)} style={S.chipX}><FiX size={12} /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div style={S.modalFooter}>
        <button style={S.btnGhost} onClick={onClose}>Cancelar</button>
        <button style={S.btn} onClick={crear} disabled={guardando}>{guardando ? 'Creando...' : 'Crear vendedor'}</button>
      </div>

      {buscandoClientes && (
        <ModalBuscarClientes
          COL={COL}
          isMobile={isMobile}
          seleccionados={clientesSeleccionados}
          onClose={() => setBuscandoClientes(false)}
          onConfirm={(sel) => { setClientesSeleccionados(sel); setBuscandoClientes(false); }}
        />
      )}
    </Modal>
  );
}

function ModalBuscarClientes({ COL, isMobile, seleccionados, onClose, onConfirm }) {
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [selIds, setSelIds] = useState(() => new Set((seleccionados || []).map((c) => c.id)));

  const S = useMemo(() => buildStyles(COL), [COL]);

  const buscar = useCallback(async (query) => {
    setCargando(true);
    try {
      const r = await API.get('/vendedores/clientes-disponibles', { params: { q: query || undefined } });
      if (r.data.status === 'success') setResultados(r.data.data);
    } catch (e) {
      /* noop */
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => buscar(q), 250);
    return () => clearTimeout(t);
  }, [q, buscar]);

  const toggle = (c) => {
    if (c.vendedor_cedula && !selIds.has(c.id)) return; // ya asignado a otro, bloqueado
    setSelIds((prev) => {
      const next = new Set(prev);
      if (next.has(c.id)) next.delete(c.id); else next.add(c.id);
      return next;
    });
  };

  const confirmar = () => {
    const sel = resultados.filter((c) => selIds.has(c.id));
    onConfirm(sel);
  };

  return (
    <Modal COL={COL} isMobile={isMobile} onClose={onClose} title="Seleccionar clientes" width={560}>
      <div style={{ position: 'relative', marginBottom: 12 }}>
        <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: COL.muted }} />
        <input
          autoFocus
          style={{ ...S.input, paddingLeft: 38 }}
          placeholder="Buscar por nombre, cédula, teléfono o RIF..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div style={{ maxHeight: isMobile ? 'calc(100vh - 320px)' : 380, overflowY: 'auto' }}>
        {cargando ? (
          <div style={{ ...S.empty, padding: 24 }}>Buscando...</div>
        ) : resultados.length === 0 ? (
          <div style={{ ...S.empty, padding: 24 }}>Sin resultados</div>
        ) : (
          resultados.map((c) => {
            const marcado = selIds.has(c.id);
            const ocupado = c.vendedor_cedula && !marcado;
            return (
              <div
                key={c.id}
                onClick={() => !ocupado && toggle(c)}
                style={{
                  ...S.clienteRow,
                  opacity: ocupado ? 0.55 : 1,
                  cursor: ocupado ? 'not-allowed' : 'pointer',
                  background: marcado ? COL.priLight : 'transparent',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <strong style={{ color: COL.text, fontSize: 14 }}>
                    {c.nombre || c.nombre_empresa || c.id}{c.apellido ? ` ${c.apellido}` : ''}
                  </strong>
                  <div style={{ fontSize: 12, color: COL.muted }}>
                    {[c.telefono, c.rif, c.cedula].filter(Boolean).join(' · ') || '—'}
                  </div>
                </div>
                {ocupado ? (
                  <span style={{ fontSize: 12, color: COL.amber, fontWeight: 600 }}>Asignado a {c.vendedor_nombre}</span>
                ) : (
                  <div style={{ width: 20, height: 20, borderRadius: 6, border: `2px solid ${marcado ? COL.pri2 : COL.border}`, background: marcado ? COL.pri2 : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {marcado && <FiCheck size={12} color="#fff" />}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <div style={S.modalFooter}>
        <span style={{ fontSize: 13, color: COL.muted }}>{selIds.size} seleccionados</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={S.btnGhost} onClick={onClose}>Cancelar</button>
          <button style={S.btn} onClick={confirmar}>Agregar</button>
        </div>
      </div>
    </Modal>
  );
}

function ModalClientes({ COL, isMobile, vendedor, onClose, onChanged }) {
  const { notificar } = useNotificaciones();
  const [asignados, setAsignados] = useState([]);
  const [q, setQ] = useState('');
  const [disponibles, setDisponibles] = useState([]);
  const [cargando, setCargando] = useState(true);

  const S = useMemo(() => buildStyles(COL), [COL]);

  const cargar = useCallback(async () => {
    try {
      const r = await API.get(`/vendedores/${vendedor.cedula}/clientes`);
      if (r.data.status === 'success') setAsignados(r.data.data.asignados || []);
    } catch (e) {
      /* noop */
    } finally {
      setCargando(false);
    }
  }, [vendedor.cedula]);

  const buscar = useCallback(async (query) => {
    try {
      const r = await API.get('/vendedores/clientes-disponibles', { params: { q: query || undefined } });
      if (r.data.status === 'success') setDisponibles(r.data.data);
    } catch (e) {
      /* noop */
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    const t = setTimeout(() => buscar(q), 250);
    return () => clearTimeout(t);
  }, [q, buscar]);

  const asignar = async (idcliente) => {
    try {
      await API.post(`/vendedores/${vendedor.cedula}/clientes`, { idcliente });
      cargar(); buscar(q);
    } catch (e) {
      notificar({ titulo: 'Error', mensaje: e.response?.data?.detail || 'No se pudo asignar', tipo: 'error' });
    }
  };

  const desasignar = async (idcliente) => {
    try {
      await API.delete(`/vendedores/${vendedor.cedula}/clientes/${idcliente}`);
      cargar(); buscar(q);
      onChanged();
    } catch (e) {
      notificar({ titulo: 'Error', mensaje: e.response?.data?.detail || 'No se pudo quitar', tipo: 'error' });
    }
  };

  return (
    <Modal COL={COL} isMobile={isMobile} onClose={onClose} title={`Clientes de ${vendedor.nombre || vendedor.cedula}`} width={560}>
      {cargando ? (
        <div style={{ ...S.empty, padding: 24 }}>Cargando...</div>
      ) : (
        <>
          <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 600, color: COL.muted }}>Asignados ({asignados.length})</div>
          <div style={{ maxHeight: 180, overflowY: 'auto', marginBottom: 16 }}>
            {asignados.length === 0 ? (
              <div style={S.empty}>Sin clientes asignados</div>
            ) : (
              asignados.map((c) => (
                <div key={c.id} style={S.clienteRow}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ color: COL.text, fontSize: 14 }}>{c.nombre || c.nombre_empresa || c.id}{c.apellido ? ` ${c.apellido}` : ''}</strong>
                    <div style={{ fontSize: 12, color: COL.muted }}>{c.telefono || c.rif || ''}</div>
                  </div>
                  <button style={{ ...S.btnGhost, color: COL.red }} onClick={() => desasignar(c.id)}><FiUserMinus /> Quitar</button>
                </div>
              ))
            )}
          </div>

          <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 600, color: COL.muted }}>Agregar clientes</div>
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: COL.muted }} />
            <input style={{ ...S.input, paddingLeft: 38 }} placeholder="Buscar cliente..." value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <div style={{ maxHeight: 220, overflowY: 'auto' }}>
            {disponibles.length === 0 ? (
              <div style={S.empty}>Sin resultados</div>
            ) : (
              disponibles.map((c) => {
                const esDeEste = c.vendedor_cedula === vendedor.cedula;
                const ocupado = c.vendedor_cedula && !esDeEste;
                return (
                  <div key={c.id} style={S.clienteRow}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <strong style={{ color: COL.text, fontSize: 14 }}>{c.nombre || c.nombre_empresa || c.id}{c.apellido ? ` ${c.apellido}` : ''}</strong>
                      <div style={{ fontSize: 12, color: COL.muted }}>{c.telefono || c.rif || ''}</div>
                    </div>
                    {ocupado ? (
                      <span style={{ fontSize: 12, color: COL.amber, fontWeight: 600 }}>De {c.vendedor_nombre}</span>
                    ) : (
                      <button style={S.btnGhost} onClick={() => asignar(c.id)}><FiUserPlus /> Asignar</button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </Modal>
  );
}

function Field({ icon, label, children }) {
  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 4 }}>
        {icon} {label}
      </label>
      {children}
    </div>
  );
}

function Modal({ COL, isMobile, onClose, title, children, width }) {
  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: COL.overlay, display: 'flex',
        alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: isMobile ? 0 : 20,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: COL.surface, borderRadius: isMobile ? 0 : 16, width: isMobile ? '100%' : width,
          maxWidth: '100%', height: isMobile ? '100%' : 'auto', maxHeight: isMobile ? '100%' : '90vh',
          display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: `1px solid ${COL.border}` }}>
          <h3 style={{ margin: 0, fontSize: 17, color: COL.text, fontWeight: 700 }}>{title}</h3>
          <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: COL.muted }}><FiX size={20} /></button>
        </div>
        <div style={{ padding: 22, overflowY: 'auto', flex: 1 }}>{children}</div>
      </div>
    </div>
  );
}

function buildStyles(COL) {
  return {
    wrap: { padding: 24, fontFamily: 'system-ui, sans-serif', background: COL.bg, minHeight: '100vh', color: COL.text },
    head: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20, gap: 12, flexWrap: 'wrap' },
    title: { fontSize: 22, fontWeight: 700, color: COL.text, margin: 0 },
    subtitle: { fontSize: 13, color: COL.muted, marginTop: 4 },
    btn: {
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 10,
      border: 'none', background: COL.pri, color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600,
      transition: 'opacity 0.15s',
    },
    btnGhost: {
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 8,
      border: `1px solid ${COL.border}`, background: COL.surface, color: COL.text, cursor: 'pointer', fontSize: 13, fontWeight: 500,
    },
    tabs: { display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' },
    tabBtn: {
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 999,
      border: `1px solid ${COL.border}`, background: COL.surface, color: COL.text, cursor: 'pointer', fontSize: 13, fontWeight: 600,
    },
    tabBtnActive: { background: COL.pri, color: '#fff', borderColor: COL.pri },
    badge: { background: COL.red, color: '#fff', borderRadius: 999, padding: '0 7px', fontSize: 11, fontWeight: 700 },
    card: { background: COL.surface, borderRadius: 16, border: `1px solid ${COL.border}`, overflow: 'hidden' },
    tableWrap: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', minWidth: 760 },
    th: { textAlign: 'left', padding: '12px 14px', borderBottom: `1px solid ${COL.border}`, color: COL.muted, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' },
    td: { padding: '13px 14px', borderBottom: `1px solid ${COL.border}`, fontSize: 14, color: COL.text, verticalAlign: 'middle' },
    tdSub: { fontSize: 12, color: COL.muted },
    pill: { padding: '3px 10px', borderRadius: 999, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap' },
    actions: { display: 'flex', gap: 6, flexWrap: 'wrap' },
    input: {
      padding: '10px 12px', borderRadius: 10, border: `1px solid ${COL.border}`, fontSize: 14,
      width: '100%', boxSizing: 'border-box', color: COL.text, background: COL.inputBg,
    },
    formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14 },
    chips: { display: 'flex', flexWrap: 'wrap', gap: 8 },
    chip: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 999, background: COL.priLight, color: COL.pri2, fontSize: 13, fontWeight: 600 },
    chipX: { border: 'none', background: 'none', cursor: 'pointer', color: COL.pri2, display: 'inline-flex', padding: 0 },
    clienteRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 4px', borderBottom: `1px solid ${COL.border}`, borderRadius: 8 },
    empty: { fontSize: 13, color: COL.muted, textAlign: 'center', padding: 16, background: COL.surfaceAlt, borderRadius: 10 },
    modalFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 20, paddingTop: 16, borderTop: `1px solid ${COL.border}` },
  };
}
